/**
 * Conversão da câmera entre o espaço do viewer e o espaço canônico do BCF.
 *
 * O BCF exige que a câmera esteja no **sistema de mundo do IFC, em metros** — é
 * isso que faz o viewpoint abrir no BIMcollab, no Solibri ou em qualquer outro
 * programa sobre o mesmo modelo. O viewer, por sua vez, desloca e escala o modelo
 * no carregamento (globalOffset para precisão de ponto flutuante, `applyScaling`,
 * `applyRefPoint`), então gravar a câmera como ela está na cena produz coordenada
 * que só funciona dentro do próprio viewer.
 *
 * A conta mora aqui, isolada e sem dependência do Forge nem do THREE, por dois
 * motivos:
 *
 * 1. É testável. O bug de 0,3048 (modelo em pés, `applyScaling: 'meters'` no
 *    load, e o restore convertendo de novo o que já estava em metros) passou
 *    despercebido por meses porque ida e volta moravam em arquivos diferentes e
 *    ninguém conseguia exercitá-las juntas.
 * 2. Ela serve aos dois motores. O ifc-lite vai precisar da mesma conversão, e
 *    ela não pode ser reescrita lá com outra convenção.
 *
 * A matriz vem SEMPRE do viewer (`getModelToViewerTransform()` e o seu inverso),
 * nunca de suposição sobre o que o carregamento fez. Foi supor que quebrou antes:
 * o `+ globalOffset` da captura era verdade até alguém acrescentar uma opção de
 * load em outro arquivo, seis meses depois.
 */

export interface IVec3 {
  x: number;
  y: number;
  z: number;
}

/**
 * Como o viewer posicionou o modelo. `elements` segue a convenção do THREE:
 * 16 números em ordem de coluna.
 */
export interface IModelPlacement {
  /** Modelo (unidade nativa do arquivo) → viewer. */
  modelToViewer: number[];
  /** Viewer → modelo (unidade nativa). */
  viewerToModel: number[];
  /** Metros por unidade do modelo: 0,3048 num arquivo em pés, 1 em metros. */
  metersPerModelUnit: number;
}

const IDENTITY = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

export const IDENTITY_PLACEMENT: IModelPlacement = {
  modelToViewer: IDENTITY,
  viewerToModel: IDENTITY,
  metersPerModelUnit: 1,
};

/** Ponto por matriz, com divisão perspectiva — igual a `Vector3.applyMatrix4`. */
export const applyMatrix = (v: IVec3, e: number[]): IVec3 => {
  const w = e[3] * v.x + e[7] * v.y + e[11] * v.z + e[15] || 1;

  return {
    x: (e[0] * v.x + e[4] * v.y + e[8] * v.z + e[12]) / w,
    y: (e[1] * v.x + e[5] * v.y + e[9] * v.z + e[13]) / w,
    z: (e[2] * v.x + e[6] * v.y + e[10] * v.z + e[14]) / w,
  };
};

/**
 * Direção por matriz: só a parte rotacional, e o resultado sai normalizado —
 * `CameraDirection` e `CameraUpVector` são unitários por definição no BCF, então
 * escala e translação não podem vazar para eles.
 */
export const transformDirection = (v: IVec3, e: number[]): IVec3 => {
  const x = e[0] * v.x + e[4] * v.y + e[8] * v.z;
  const y = e[1] * v.x + e[5] * v.y + e[9] * v.z;
  const z = e[2] * v.x + e[6] * v.y + e[10] * v.z;

  return normalize({ x, y, z });
};

export const normalize = (v: IVec3): IVec3 => {
  const length = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);

  return length > 0
    ? { x: v.x / length, y: v.y / length, z: v.z / length }
    : { x: 0, y: 0, z: 0 };
};

/**
 * Posição do viewer → posição do BCF (mundo do IFC, em metros).
 *
 * Duas etapas: desfazer tudo que o viewer aplicou (offset, placement, ponto de
 * referência, escala) e depois levar da unidade nativa do arquivo para metros.
 */
export const viewerPointToBcf = (point: IVec3, placement: IModelPlacement): IVec3 => {
  const inModelUnits = applyMatrix(point, placement.viewerToModel);
  const scale = placement.metersPerModelUnit;

  return { x: inModelUnits.x * scale, y: inModelUnits.y * scale, z: inModelUnits.z * scale };
};

/** O caminho de volta: BCF (metros) → posição no viewer. */
export const bcfPointToViewer = (point: IVec3, placement: IModelPlacement): IVec3 => {
  const scale = placement.metersPerModelUnit || 1;
  const inModelUnits = { x: point.x / scale, y: point.y / scale, z: point.z / scale };

  return applyMatrix(inModelUnits, placement.modelToViewer);
};

/**
 * Direções não levam escala nem translação, mas levam rotação — modelo colocado
 * girado no viewer tem eixos diferentes dos do arquivo.
 */
export const viewerDirectionToBcf = (v: IVec3, placement: IModelPlacement): IVec3 =>
  transformDirection(v, placement.viewerToModel);

export const bcfDirectionToViewer = (v: IVec3, placement: IModelPlacement): IVec3 =>
  transformDirection(v, placement.modelToViewer);

/**
 * Comprimento (altura da vista ortográfica, distância) do viewer para metros do
 * BCF. Só a escala: `ViewToWorldScale` é altura em metros.
 */
export const viewerLengthToBcf = (length: number, placement: IModelPlacement): number => {
  const viewerPerModelUnit = matrixScale(placement.modelToViewer);

  return viewerPerModelUnit > 0
    ? (length / viewerPerModelUnit) * placement.metersPerModelUnit
    : length;
};

export const bcfLengthToViewer = (length: number, placement: IModelPlacement): number => {
  const viewerPerModelUnit = matrixScale(placement.modelToViewer);
  const scale = placement.metersPerModelUnit || 1;

  return viewerPerModelUnit > 0 ? (length / scale) * viewerPerModelUnit : length;
};

/** Plano no viewer como o Forge guarda: `normal·p + constant = 0`, normal unitária. */
export interface IViewerPlane {
  normal: IVec3;
  constant: number;
}

/** Plano no BCF: um ponto do plano e a normal, no mesmo espaço da câmera. */
export interface IBcfPlane {
  location: IVec3;
  direction: IVec3;
}

/**
 * Plano de corte do viewer → BCF. Um ponto do plano vai pelo caminho da posição
 * e a normal pelo da direção — o mesmo espaço da câmera, sem conta própria.
 */
export const viewerPlaneToBcf = (plane: IViewerPlane, placement: IModelPlacement): IBcfPlane => {
  const { normal, constant } = plane;
  const pointOnPlane = { x: -constant * normal.x, y: -constant * normal.y, z: -constant * normal.z };

  return {
    location: viewerPointToBcf(pointOnPlane, placement),
    direction: viewerDirectionToBcf(normal, placement),
  };
};

export const bcfPlaneToViewer = (plane: IBcfPlane, placement: IModelPlacement): IViewerPlane => {
  const point = bcfPointToViewer(plane.location, placement);
  const normal = bcfDirectionToViewer(plane.direction, placement);

  return {
    normal,
    constant: -(normal.x * point.x + normal.y * point.y + normal.z * point.z),
  };
};

/**
 * Fator de escala uniforme da matriz — o comprimento da primeira coluna, que é a
 * imagem do eixo X. Serve porque as transformações do viewer são semelhança
 * (escala uniforme, rotação e translação); escala não uniforme não é caso real
 * aqui e distorceria a câmera de qualquer jeito.
 */
const matrixScale = (e: number[]): number =>
  Math.sqrt(e[0] * e[0] + e[1] * e[1] + e[2] * e[2]);

/**
 * Monta o placement a partir de um modelo do Forge. Quando o viewer não sabe
 * responder (bundle antigo, modelo ainda sem dados), cai para a translação pura
 * do `globalOffset` com escala 1 — que é a suposição de antes e continua correta
 * enquanto o load normalizar para metros.
 */
export const placementFromForgeModel = (model: any): IModelPlacement => {
  if (!model) { return IDENTITY_PLACEMENT; }

  const modelToViewer = typeof model.getModelToViewerTransform === 'function'
    ? model.getModelToViewerTransform()?.elements
    : null;
  const viewerToModel = typeof model.getInverseModelToViewerTransform === 'function'
    ? model.getInverseModelToViewerTransform()?.elements
    : null;

  if (!modelToViewer || !viewerToModel) {
    const go = model.getData?.()?.globalOffset ?? { x: 0, y: 0, z: 0 };

    return {
      modelToViewer: translation(-(go.x || 0), -(go.y || 0), -(go.z || 0)),
      viewerToModel: translation(go.x || 0, go.y || 0, go.z || 0),
      metersPerModelUnit: 1,
    };
  }

  // O fator de metros sai da PRÓPRIA matriz, não de `getUnitScale()`.
  //
  // O `InternalViewer` carrega com `applyScaling: 'meters'`, então o espaço do
  // viewer já é métrico e a escala da matriz (unidades de viewer por unidade do
  // modelo) É o metros-por-unidade-do-modelo. Tirar dos dois lugares diferentes
  // abre espaço para eles discordarem: se o `getUnitScale()` reportar a unidade
  // depois da normalização (1) enquanto a matriz carrega a conversão de pés
  // (0,3048), a ida multiplica por um fator e a volta divide por outro, e a
  // câmera pousa fora do lugar.
  //
  // Da matriz, o fator cancela exatamente na composição: o que sobra da conversão
  // é desfazer translação e rotação do placement — que é tudo que falta, já que a
  // cena está em metros. É também o que mantém a compatibilidade com o histórico,
  // gravado como `posição + globalOffset`.
  const metersPerModelUnit = matrixScale(Array.from(modelToViewer)) || 1;

  return {
    modelToViewer: Array.from(modelToViewer),
    viewerToModel: Array.from(viewerToModel),
    metersPerModelUnit,
  };
};

export const translation = (x: number, y: number, z: number): number[] =>
  [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, x, y, z, 1];
